import { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, serverTimestamp, query, orderBy, limit } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Link2, CheckCircle, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Props {
  reportType: 'market_study' | 'strategic_plan' | 'deal_quality' | 'market_comparison' | 'financing_feasibility';
  reportContent: any;
  reportTitle: string;
  language?: 'en' | 'fr';
}

const REPORT_PRICES: Record<string, number> = {
  market_study: 199,
  strategic_plan: 249,
  deal_quality: 49,
  market_comparison: 99,
  financing_feasibility: 79,
};

const REPORT_PRICE_IDS: Record<string, string> = {
  market_study: 'price_1Td1NlCkUJGKtqQAoHqgWzsC',
  strategic_plan: 'price_1Td1OOCkUJGKtqQANRzElYzu',
  deal_quality: 'price_1Td1KACkUJGKtqQAHxG44n8u',
  market_comparison: 'price_1Td1LSCkUJGKtqQAjWK6W8YR',
  financing_feasibility: 'price_1Td1KpCkUJGKtqQAwzvIdGzV',
};

interface Deal {
  id: string;
  businessName: string;
  industry: string;
}

export function AttachReportToListing({ reportType, reportContent, reportTitle, language = 'en' }: Props) {
  const { toast } = useToast();
  const [deals, setDeals] = useState<Deal[]>([]);
  const [selectedDealId, setSelectedDealId] = useState('');
  const [attaching, setAttaching] = useState(false);
  const [attached, setAttached] = useState(false);
  const [loading, setLoading] = useState(true);

  const labels = {
    en: {
      title: 'Attach to Deal Listing',
      selectDeal: 'Select a deal...',
      attach: 'Attach Report to Listing',
      attaching: 'Attaching...',
      attached: 'Attached to Listing ✓',
      success: 'Report attached — buyers can now purchase it on the listing.',
      error: 'Failed to attach report.',
      price: 'Will appear at',
      noDeal: 'No deals available.',
    },
    fr: {
      title: 'Attacher au Listing',
      selectDeal: 'Sélectionner un deal...',
      attach: 'Attacher le Rapport au Listing',
      attaching: 'Attachement...',
      attached: 'Attaché au Listing ✓',
      success: 'Rapport attaché — les acheteurs peuvent maintenant l\'acheter sur le listing.',
      error: 'Échec de l\'attachement.',
      price: 'Apparaîtra à',
      noDeal: 'Aucun deal disponible.',
    },
  }[language];

  useEffect(() => {
    const loadDeals = async () => {
      try {
        const snap = await getDocs(
          query(collection(db, 'deals'), orderBy('createdAt', 'desc'), limit(50))
        );
        setDeals(
          snap.docs.map(d => ({
            id: d.id,
            businessName: d.data().businessName || d.data().companyName || d.id,
            industry: d.data().industry || '',
          }))
        );
      } catch (e) {
        console.error('Error loading deals:', e);
      } finally {
        setLoading(false);
      }
    };
    loadDeals();
  }, []);

  const handleAttach = async () => {
    if (!selectedDealId || !reportContent) return;
    setAttaching(true);
    try {
      // Save report to dealReports/{dealId}/{reportType}
      await setDoc(
        doc(db, 'dealReports', selectedDealId, 'reports', reportType),
        {
          dealId: selectedDealId,
          reportType,
          reportTitle,
          content: reportContent,
          priceId: REPORT_PRICE_IDS[reportType],
          price: REPORT_PRICES[reportType],
          status: 'published',
          publishedAt: serverTimestamp(),
          generatedAt: serverTimestamp(),
        }
      );

      // Also update deals/{dealId} to flag this report as available
      await setDoc(
        doc(db, 'deals', selectedDealId),
        {
          [`availableReports.${reportType}`]: {
            priceId: REPORT_PRICE_IDS[reportType],
            price: REPORT_PRICES[reportType],
            publishedAt: new Date().toISOString(),
            title: reportTitle,
          },
        },
        { merge: true }
      );

      setAttached(true);
      toast({ title: labels.success });
    } catch (e) {
      console.error(e);
      toast({ title: labels.error, variant: 'destructive' });
    } finally {
      setAttaching(false);
    }
  };

  if (!reportContent) return null;

  return (
    <div className="rounded-lg border border-border bg-card p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <Link2 className="h-4 w-4" />
          {labels.title}
        </h4>
        <Badge variant="secondary">
          ${REPORT_PRICES[reportType]}
        </Badge>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading deals...
        </div>
      ) : deals.length === 0 ? (
        <p className="text-sm text-muted-foreground">{labels.noDeal}</p>
      ) : (
        <div className="space-y-3">
          <Select value={selectedDealId} onValueChange={setSelectedDealId}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder={labels.selectDeal} />
            </SelectTrigger>
            <SelectContent>
              {deals.map(deal => (
                <SelectItem key={deal.id} value={deal.id}>
                  {deal.businessName}
                  {deal.industry ? ` — ${deal.industry}` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            onClick={handleAttach}
            disabled={!selectedDealId || attaching || attached}
            className="w-full"
            variant={attached ? 'outline' : 'default'}
          >
            {attached ? (
              <><CheckCircle className="h-4 w-4 mr-2" />{labels.attached}</>
            ) : attaching ? (
              <><Loader2 className="h-4 w-4 animate-spin mr-2" />{labels.attaching}</>
            ) : (
              <><Link2 className="h-4 w-4 mr-2" />{labels.attach}</>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
